/**
 * SignIn — Email + OTP login form with OAuth provider buttons.
 *
 * **Matches v1 production layout** from `/auth/signin`:
 *   - Logo at top (company-icon.png, 120×120)
 *   - "Sign in with email" heading (email step) / "Code from email" (OTP step)
 *   - Single text field with inline submit arrow (InputAdornment-style)
 *   - "or" divider (narrow, subtle)
 *   - OAuth buttons sorted alphabetically in 2×2 grid
 *   - Registration notice + Terms link
 *   - Footer links (Go · Support · Affiliate)
 *
 * NEVER-08: Preserves intended destination through the auth flow.
 * NEVER-11: All user-facing text uses i18n t().
 */

import { useState, useRef, useEffect, useCallback, type FormEvent } from "react"
import { type DXCUserInteraction, resolveText } from "dexie-cloud-addon"
import { useSearchParams } from "react-router-dom"
import ArrowRightCircleIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/arrow-right-circle.svg?react"
import ArrowLeftIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/arrow-left.svg?react"
import { CircularProgress } from "@/app/components/ui/CircularProgress"
import { useTranslation } from "react-i18next"
import { Capacitor } from "@capacitor/core"
import { cn } from "@/shared/utils/general/cn"
import {
  GOOGLE_AUTH_URI,
  GITHUB_AUTH_URI,
  APPLE_AUTH_URI,
  MICROSOFT_AUTH_URI,
  API_BASE_URL,
} from "@/config/environment"
import { db } from "@/db/schema"
import { markLoginStart } from "@/app/hooks/usePostLoginCleanup"
import { OAuthProviderButtons } from "./OAuthProviderButtons"

interface SignInProps {
  fields: DXCUserInteraction["fields"]
  onSubmit: DXCUserInteraction["onSubmit"]
  onCancel?: DXCUserInteraction["onCancel"]
  type: DXCUserInteraction["type"]
  alerts: DXCUserInteraction["alerts"]
}

export function SignIn({
  fields,
  onSubmit,
  onCancel,
  type,
  alerts,
}: SignInProps) {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [params, setParams] = useState<Record<string, string>>({})
  const [submitted, setSubmitted] = useState(false)
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(true)
  const [submissionSlow, setSubmissionSlow] = useState(false)
  const firstInputRef = useRef<HTMLInputElement>(null)

  // Pre-fill email from invited-email query param (NEVER-08)
  useEffect(() => {
    const invitedEmail = searchParams.get("invited-email")
    if (invitedEmail && type === "email") {
      setParams((prev) => ({ ...prev, email: invitedEmail }))
    }
  }, [searchParams, type])

  // Mark login start time for performance measurement (v1 pattern)
  useEffect(() => {
    if (type === "email") {
      markLoginStart()
    }
  }, [type])

  // Keyboard resize: switch to Body mode on sign-in (v1 pattern)
  // Body mode pushes content up when keyboard opens, which is better
  // for the login form. Reverts to Native mode on unmount.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    if (!Capacitor.isPluginAvailable("Keyboard")) return

    let cleanup: (() => void) | undefined
    let keyboardModule: typeof import("@capacitor/keyboard") | undefined

    import("@capacitor/keyboard")
      .then((mod) => {
        keyboardModule = mod
        // setResizeMode is iOS-only — Android throws UNIMPLEMENTED (resize is
        // controlled by windowSoftInputMode there). Catch either way so the
        // rejection never escapes as an unhandled promise error.
        void mod.Keyboard.setResizeMode({ mode: mod.KeyboardResize.Body }).catch(() => {
          // Best-effort
        })
        cleanup = () => {
          // Hide keyboard first, wait for iOS to finish the animation,
          // then restore Native resize mode. Without the delay, WKWebView
          // can get stuck at the keyboard-open height leaving a white gap.
          void mod.Keyboard.hide().then(() => {
            return new Promise<void>(resolve => setTimeout(resolve, 300))
          }).then(() => {
            return mod.Keyboard.setResizeMode({ mode: mod.KeyboardResize.Native })
          }).catch(() => {
            // Best-effort
          })
        }
      })
      .catch(() => {
        // Keyboard plugin not available
      })

    return () => {
      if (cleanup) {
        cleanup()
      } else if (keyboardModule) {
        // Import finished but cleanup wasn't assigned yet (race)
        void keyboardModule.Keyboard.hide().catch(() => {})
        void keyboardModule.Keyboard.setResizeMode({ mode: keyboardModule.KeyboardResize.Native }).catch(() => {})
      }
    }
  }, [])

  // Native deep link login — Capacitor redirects with ?email=&otp=&otpId=
  useEffect(() => {
    const email = searchParams.get("email")
    const otp = searchParams.get("otp")
    const otpId = searchParams.get("otpId")

    if (email && otp && otpId) {
      void db.cloud.login({ email, otp, otpId }).catch(() => {
        // Login failed — let user try manually
      })
    }
  }, [searchParams])

  // Reset submitted state when interaction type changes (email → otp)
  // so the OTP input is not stuck disabled from the previous submission
  useEffect(() => {
    setSubmitted(false)
    setSubmissionSlow(false)
  }, [type])

  // Reset submitted state when error alerts arrive (e.g. INVALID_EMAIL)
  // so the input is re-enabled and the user can correct and resubmit.
  useEffect(() => {
    if (alerts.length > 0) {
      setSubmitted(false)
      setSubmissionSlow(false)
    }
  }, [alerts])

  // Show "taking longer" message after 8s of spinner on email step
  useEffect(() => {
    if (!submitted || type !== "email") return
    const timer = setTimeout(() => setSubmissionSlow(true), 8000)
    return () => clearTimeout(timer)
  }, [submitted, type])

  // Auto-focus first input
  useEffect(() => {
    firstInputRef.current?.focus()
  }, [type])

  // Auto-submit OTP at 8 chars (v1 pattern)
  useEffect(() => {
    if (!shouldAutoSubmit || type !== "otp") return
    const otpValue = params["otp"]
    if (otpValue?.length === 8 && onSubmit) {
      onSubmit(params)
      setSubmitted(true)
    }
  }, [params, shouldAutoSubmit, type, onSubmit])

  // Disable auto-submit on errors
  useEffect(() => {
    if (alerts.some((a) => a.type === "error")) {
      setShouldAutoSubmit(false)
    }
  }, [alerts])

  const handleSubmit = useCallback(
    (ev: FormEvent) => {
      ev.preventDefault()
      if (!onSubmit) return
      if (type === "otp" && !params["otp"]?.length) return

      setSubmitted(true)
      onSubmit(params)
    },
    [onSubmit, params, type],
  )

  const handleFieldChange = useCallback((fieldName: string, value: string) => {
    const trimmed =
      fieldName === "otp"
        ? value.replace(/\s/g, "").toUpperCase().slice(0, 8)
        : fieldName === "email"
          ? value.toLowerCase().trim()
          : value.trim()
    setParams((prev) => ({ ...prev, [fieldName]: trimmed }))
    setShouldAutoSubmit(true)
  }, [])

  // Determine if we're on a small screen (matches v1 breakpoint behavior)
  // v1 used full-screen on mobile, rounded card on desktop
  const isSmall = typeof window !== "undefined" && window.innerWidth < 768

  return (
    <>
    <div className="flex min-h-screen w-full items-center justify-center flex-col bg-foreground/5">
      {/* Card — matches v1: rounded-[30px], shadow, min 700×700 on desktop, full screen on mobile */}
      <div
        className={cn(
          "flex flex-col items-center justify-center bg-background",
          isSmall
            ? "min-h-screen w-full px-4"
            : "min-w-[700px] min-h-[700px] rounded-[30px] px-[50px] pb-[10px]",
        )}
        style={
          isSmall
            ? undefined
            : { boxShadow: "0 11px 34px 0 rgba(0, 0, 0, 0.2)" }
        }
      >
        {/* Logo */}
        <div className="w-full text-center">
          <img
            src="/logos/company-icon.png"
            alt="ToToDo"
            className="mx-auto"
            style={{ maxWidth: 120, maxHeight: 120 }}
          />
        </div>

        {/* Form area */}
        <div className="w-full text-center">
          {Object.entries(
            fields as Record<
              string,
              { type: string; label: string; placeholder: string }
            >,
          ).map(([fieldName, { type: fieldType }]) => (
            <div key={fieldName}>
              {/* Heading */}
              <h1 className="text-[28px] font-bold mt-2 mb-4">
                {fieldType === "otp"
                  ? t("Code from email")
                  : t("Sign in with email")}
              </h1>

              {/* Input with inline submit arrow — matches v1 */}
              <form onSubmit={handleSubmit} className="flex justify-center">
                <div className="relative w-full max-w-[350px]">
                  <input
                    ref={firstInputRef}
                    type={fieldType}
                    name={fieldName}
                    disabled={!onSubmit || submitted}
                    placeholder={
                      fieldType === "otp"
                        ? t("Paste code from email")
                        : t("E-mail")
                    }
                    autoComplete={
                      fieldType === "otp" ? "one-time-code" : "email"
                    }
                    value={params[fieldName] ?? ""}
                    onChange={(e) =>
                      handleFieldChange(fieldName, e.target.value)
                    }

                    className={cn(
                      "w-full rounded-[10px] border border-foreground/20 bg-background",
                      "pl-6 pr-12 py-[16.5px] text-xl",
                      "placeholder:text-foreground/30",
                      "hover:border-foreground/40",
                      "focus:border-transparent focus:outline focus:outline-2 focus:outline-primary",
                      "disabled:cursor-not-allowed disabled:opacity-50",
                    )}
                  />
                  {/* Submit arrow / circular loading inside input */}
                  <button
                    type="submit"
                    disabled={!onSubmit}
                    className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer disabled:cursor-default"
                    tabIndex={-1}
                  >
                    {!onSubmit || submitted ? (
                      <CircularProgress className="size-6" />
                    ) : (
                      <ArrowRightCircleIcon className="size-8 text-foreground/30" />
                    )}
                  </button>
                </div>
              </form>

              {/* OAuth providers — only on email step, NOT on OTP step */}
              {fieldType !== "otp" && (
                <>
                  {/* Divider — "or" — matches v1: width 15%, margin 10px auto, opacity 0.5 */}
                  <div className="flex items-center justify-center my-[10px] w-[15%] mx-auto opacity-50">
                    <div className="flex-1 border-t border-foreground/15" />
                    <span className="px-2 text-[11px] text-foreground/30">
                      {t("or")}
                    </span>
                    <div className="flex-1 border-t border-foreground/15" />
                  </div>

                  <OAuthProviderButtons
                    googleUri={GOOGLE_AUTH_URI}
                    githubUri={GITHUB_AUTH_URI}
                    appleUri={APPLE_AUTH_URI}
                    microsoftUri={MICROSOFT_AUTH_URI}
                  />
                </>
              )}

              {/* Alerts — render error, warning, and info */}
              {alerts.map((alert, i) => (
                <div
                  key={i}
                  className={cn(
                    "max-w-[300px] mx-auto mt-4 rounded-lg px-4 py-3 text-sm",
                    alert.type === "error" &&
                      "bg-destructive/10 text-destructive",
                    alert.type === "warning" &&
                      "bg-[var(--yellow-500)]/10 text-[var(--yellow-500)]",
                    alert.type === "info" && "bg-primary/10 text-primary",
                  )}
                >
                  {resolveText(alert)?.replace(
                    "Invalid OTP given",
                    t("Invalid email code"),
                  )}
                </div>
              ))}

              {/* Slow submission feedback — shown after 8s of spinner on email step */}
              {submissionSlow && submitted && fieldType !== "otp" && (
                <p className="mt-3 text-center text-xs text-muted-foreground animate-in fade-in duration-300">
                  {t("Taking longer than expected...")}
                </p>
              )}

              {/* Registration notice + Terms — matches v1: marginTop 25px, fontSize 12px, default line-height */}
              <div className="mt-[25px] text-center text-[12px] italic text-muted-foreground leading-relaxed">
                {fieldType === "otp" ? (
                  <>
                    {t("We just sent you a temporary login code!")}
                    <br />
                    {t("Check your email.")}
                  </>
                ) : (
                  <>
                    {t("Sign up is done automatically on sign in.")}
                    <br />
                    {t("By signing up you agree to our")}{" "}
                    <a
                      href="/terms"
                      target="_blank"
                      className="font-bold underline text-muted-foreground"
                    >
                      {t("Terms of Service")}.
                    </a>
                  </>
                )}
              </div>

              {/* Back arrow on OTP step — matches v1 */}
              {fieldType === "otp" && onCancel && (
                <button
                  type="button"
                  className="mt-12 cursor-pointer p-2 mx-auto block"
                  onClick={() => onCancel()}
                >
                  <ArrowLeftIcon className="size-12 text-foreground/25" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer — Go · Support · Affiliate — matches v1: mt 5px, padding 12px 16px, opacity 0.5, navWidth 60px */}
      {!isSmall && (
        <div className="flex items-center justify-center mt-[5px] py-3 px-4 opacity-50 text-[12px] font-light">
          <div className="flex justify-center items-center">
            <div className="w-[60px] text-right">
              <a
                onClick={() => window.open(`${API_BASE_URL}/go`, "_blank")}
                className="cursor-pointer text-foreground/60 hover:text-primary"
              >
                Go
              </a>
            </div>
            <span className="text-foreground/30 mx-1 px-[15px]"> · </span>
            <div className="w-[60px] text-center">
              <a
                onClick={() => window.open(`${API_BASE_URL}/support`, "_blank")}
                className="cursor-pointer text-foreground/60 hover:text-primary"
              >
                Support
              </a>
            </div>
            <span className="text-foreground/30 mx-1 px-[15px]"> · </span>
            <div className="w-[60px] text-left">
              <a
                onClick={() =>
                  window.open(`${API_BASE_URL}/affiliate`, "_blank")
                }
                className="cursor-pointer text-foreground/60 hover:text-primary"
              >
                Affiliate
              </a>
            </div>
          </div>
        </div>
      )}
    </div>

    </>
  )
}
